/**
 * The review inbox: every waiting application's open questions in one
 * place. Twenty applications that each stopped on "Are you serving a notice
 * period?" ask it once here; one answer goes to each of them as that
 * portal's own choice, is kept for the next employer, and every
 * application that is then complete goes back on the queue.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { applicationApi, profileApi, referenceApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, PageSpinner } from '@/components/ui/Feedback'
import { Combobox } from '@/components/ui/Select'
import { Modal } from '@/components/ui/Modal'
import { PageHeader, Section } from '@/components/ui/Page'
import { cn, relativeTime } from '@/lib/utils'
import { isLicenseError } from '@/store/license'
import { toast } from '@/store/toast'
import { refreshLicense } from '@/features/billing/useLicense'
import { LEVEL_LABELS, type BankEntry, type QuestionLevel, type ReviewApplication, type ReviewQuestion, type ScreenBlockerNeeds, type WaitingFor } from '@/types/api'
import { QuestionField, SparkleIcon } from './QuestionField'
import { NEEDS_META } from './ScreenStateCard'

const WAITING_LABEL: Record<WaitingFor, string> = {
  answers: 'Needs answers',
  ready: 'Ready to submit',
  review: 'Ready to retry',
  verification: 'Verify submission',
  captcha: 'CAPTCHA on their site',
  rehearsal: 'Test run',
  auto: 'Retry scheduled',
  other: 'Waiting',
}
const WAITING_TONE: Record<WaitingFor, string> = {
  answers: 'bg-warning-soft text-warning',
  ready: 'bg-brand-soft text-brand',
  review: 'bg-surface-muted text-ink-soft',
  verification: 'bg-accent-soft text-ink',
  captcha: 'bg-danger-soft text-danger',
  rehearsal: 'bg-surface-muted text-ink-muted',
  auto: 'bg-info/12 text-info',
  other: 'bg-surface-muted text-ink-muted',
}

/** "14 h left", "35 min left", or "any moment now" once the time has passed. */
function timeLeft(iso: string | null | undefined): string | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(ms)) return null
  if (ms <= 60_000) return 'any moment now'
  const minutes = Math.round(ms / 60_000)
  if (minutes < 60) return `${minutes} min left`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours} h left`
  return `${Math.round(hours / 24)} days left`
}

/** The full page: the panel, then the answer bank. */
export function ReviewInboxPage() {
  const bank = useQuery({ queryKey: ['profile-answers'], queryFn: profileApi.answers })
  const [showBank, setShowBank] = useState(false)
  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Action center"
        title="Needs review"
        description="Answer missing questions, check stopped applications, and add everything ready to your application queue."
      />
      <ReviewInboxPanel />
      <Section
        title="Your saved answers"
        description="Manage answers you have confirmed. We reuse them when an employer asks the same question."
        actions={
          <button type="button" className="text-[13px] font-medium text-brand" onClick={() => setShowBank((v) => !v)}>
            {showBank ? 'Hide' : `Show ${bank.data?.length ?? ''}`}
          </button>
        }
      >
        {showBank && <SavedAnswers entries={bank.data ?? []} />}
      </Section>
    </div>
  )
}

/**
 * The inbox itself: the open questions of every waiting application, one
 * answer each, and the applications with what they wait for. `embedded`
 * (the Today page): a heading of its own, the first questions with a
 * "show all", and the applications folded into one line.
 */
export function ReviewInboxPanel({ embedded = false }: { embedded?: boolean }) {
  const queryClient = useQueryClient()
  const inbox = useQuery({ queryKey: ['review-inbox'], queryFn: applicationApi.reviewInbox, refetchInterval: 20_000 })
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [showAll, setShowAll] = useState(!embedded)
  const [showApplications, setShowApplications] = useState(!embedded)
  const [applicationFilter, setApplicationFilter] = useState<WaitingFor | 'all'>('all')

  // The bank's word, or the model's draft, starts each question off; what the person typed is kept across refetches.
  useEffect(() => {
    const questions = inbox.data?.questions ?? []
    setAnswers((current) => {
      const next = { ...current }
      for (const q of questions) {
        if (next[q.groupKey] === undefined && q.previousAnswer) next[q.groupKey] = q.previousAnswer
      }
      return next
    })
  }, [inbox.data])

  const submit = useMutation({
    mutationFn: (send: boolean) =>
      applicationApi.submitReviewAnswers(
        Object.entries(answers)
          .filter(([, value]) => value.trim())
          .map(([groupKey, answerValue]) => ({ groupKey, answerValue })),
        send,
      ),
    onSuccess: (result, send) => {
      const sent = result.queued.length + result.retried.length
      if (send && sent) toast.success(`${sent} application${sent === 1 ? '' : 's'} queued`, `Processed one at a time.${result.stillOpen.length ? ` ${result.stillOpen.length} still need attention below.` : ''}`)
      else toast.success('Answers saved', `${result.answered} answer${result.answered === 1 ? '' : 's'} filled in${result.stillOpen.length ? `; ${result.stillOpen.length} application${result.stillOpen.length === 1 ? ' still waits' : 's still wait'}` : ''}.`)
      if (result.unmatched.length) {
        toast.info('Some portals list other choices', 'Pick from their options under the question to finish those.')
      }
      setAnswers((current) => Object.fromEntries(Object.entries(current).filter(([key]) => result.unmatched.some((item) => item.groupKey === key))))
      void queryClient.invalidateQueries({ queryKey: ['review-inbox'] })
      void queryClient.invalidateQueries({ queryKey: ['profile-answers'] })
      void queryClient.invalidateQueries({ queryKey: ['applications'] })
      void queryClient.invalidateQueries({ queryKey: ['application-stats'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] })
    },
    onError: (error: ApiError) => {
      if (isLicenseError(error)) refreshLicense(queryClient)
      toast.error('Could not save the answers', error.message)
    },
  })

  // "Retry all": every stopped application goes back on the queue, whatever it waits for.
  const [retryOpen, setRetryOpen] = useState(false)
  const [includeUnconfirmed, setIncludeUnconfirmed] = useState(false)
  const retryAll = useMutation({
    mutationFn: (unconfirmed: boolean) => applicationApi.retryAll(unconfirmed),
    onSuccess: (result) => {
      const { queued, skippedUnconfirmed } = result.counts
      toast.success(
        `Retrying ${queued} application${queued === 1 ? '' : 's'}`,
        skippedUnconfirmed ? `${skippedUnconfirmed} that may already have been sent ${skippedUnconfirmed === 1 ? 'was' : 'were'} left for you to check.` : 'Each one runs again from the start; the applied time is set when it is sent.',
      )
      setRetryOpen(false)
      setIncludeUnconfirmed(false)
      void queryClient.invalidateQueries({ queryKey: ['review-inbox'] })
      void queryClient.invalidateQueries({ queryKey: ['applications'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] })
    },
    onError: (error: ApiError) => {
      if (isLicenseError(error)) refreshLicense(queryClient)
      toast.error('Could not retry them', error.message)
    },
  })

  const filled = useMemo(() => (inbox.data?.questions ?? []).filter((q) => answers[q.groupKey]?.trim()).length, [answers, inbox.data])
  const data = inbox.data

  if (inbox.isPending) return embedded ? null : <PageSpinner label="Collecting the open questions" />
  if (inbox.isError) return embedded ? null : <ErrorState title="Could not load the review inbox" description={(inbox.error as ApiError).message} onRetry={() => inbox.refetch()} />
  if (!data) return null
  if (embedded && data.applications.length === 0) return null

  const requiredOpen = data.questions.filter((q) => q.isRequired && !(answers[q.groupKey] || '').trim()).length
  const sendable = data.summary.ready + data.summary.needsReview
  const shownQuestions = showAll ? data.questions : data.questions.slice(0, EMBEDDED_QUESTIONS)
  const stopped = data.applications.filter((a) => a.waitingFor === 'review')
  const unconfirmed = data.applications.filter((a) => a.waitingFor === 'verification').length
  const retryable = data.applications.length
  const retryButton = retryable > 0 && (
    <Button variant="secondary" size="sm" onClick={() => setRetryOpen(true)} disabled={retryAll.isPending || submit.isPending}>
      {retryAll.isPending ? 'Retrying…' : `Retry all (${retryable})`}
    </Button>
  )

  return (
    <div className="space-y-6">
      <Modal
        open={retryOpen}
        onClose={() => setRetryOpen(false)}
        title={`Retry ${retryable - (includeUnconfirmed ? 0 : unconfirmed)} application${retryable - (includeUnconfirmed ? 0 : unconfirmed) === 1 ? '' : 's'}?`}
        description="Add waiting applications to the queue for another attempt, one at a time. Saved answers are kept. Applications missing required answers may return here."
        size="sm"
        dismissible={!retryAll.isPending}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setRetryOpen(false)} disabled={retryAll.isPending}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => retryAll.mutate(includeUnconfirmed)} disabled={retryAll.isPending}>
              {retryAll.isPending ? 'Retrying…' : 'Retry them'}
            </Button>
          </div>
        }
      >
        {unconfirmed > 0 ? (
          <label className="flex items-start gap-3 text-[13.5px] text-ink">
            <input type="checkbox" className="mt-0.5" checked={includeUnconfirmed} onChange={(e) => setIncludeUnconfirmed(e.target.checked)} />
            <span>
              Also retry the {unconfirmed} that may already have been submitted (I checked on the employer's site; nothing arrived).
              <span className="block text-[12.5px] text-ink-muted">Left unticked, those stay here for you to check — sending twice is worse than once.</span>
            </span>
          </label>
        ) : (
          <p className="text-[13.5px] text-ink-muted">Applications with an uncertain submission stay out of this retry unless you explicitly include them.</p>
        )}
      </Modal>
      {embedded && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-[18px] font-semibold tracking-tight text-ink">
              {data.summary.openQuestions > 0
                ? `${data.summary.openQuestions} question${data.summary.openQuestions === 1 ? '' : 's'} holding up your applications`
                : `${data.summary.waiting} application${data.summary.waiting === 1 ? '' : 's'} waiting on you`}
            </h2>
            <p className="mt-0.5 text-[13px] text-ink-muted">
              Only what the portals insist on and nothing in your profile answers. Optional boxes are filled or left empty for you. Answer each once; everything complete — and every run that stopped on its site — joins the queue.
            </p>
          </div>
          <Link to="/dashboard/review" className="text-[13px] font-medium text-brand">
            Open Needs review
          </Link>
        </div>
      )}

      <div className="rounded-card border border-brand/20 bg-brand-soft/40 p-4 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-xl">
            <h2 className="text-[16px] font-semibold text-ink">Review once. Keep your applications moving.</h2>
            <p className="mt-1 text-[13px] leading-relaxed text-ink-muted">Answer the questions below, then queue all ready applications. Each application runs one at a time in the background. Anything needing more information stays here with its next step.</p>
          </div>
          <Button onClick={() => submit.mutate(true)} loading={submit.isPending} disabled={retryAll.isPending || (filled === 0 && sendable === 0)} className="shrink-0">
            {filled ? 'Save answers & queue all ready' : `Queue all ready (${sendable})`}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3 sm:grid-cols-5">
        <Tile label="Waiting" value={data.summary.waiting} />
        <Tile label="Need answers" value={data.summary.needsAnswers} tone="warning" />
        <Tile label="Ready to submit" value={data.summary.ready} tone="brand" />
        <Tile label="Ready to retry" value={data.summary.needsReview} />
        <Tile label="Blocking questions" value={data.summary.openQuestions} tone="warning" />
      </div>

      {data.questions.length === 0 ? (
        <EmptyState
          title={data.summary.ready || data.summary.needsReview ? 'Nothing to answer' : 'Nothing is waiting on you'}
          description={
            data.summary.ready || data.summary.needsReview
              ? 'Every question is answered. Submit the ready applications and retry the stopped ones below.'
              : 'Applications that stop on a question will collect here.'
          }
          action={
            sendable || retryable ? (
              <div className="flex flex-wrap items-center justify-center gap-2">
                {sendable > 0 && (
                  <Button onClick={() => submit.mutate(true)} disabled={submit.isPending || retryAll.isPending}>
                    {submit.isPending ? 'Adding to queue…' : `Queue ${sendable} application${sendable === 1 ? '' : 's'}`}
                  </Button>
                )}
                {retryButton}
              </div>
            ) : undefined
          }
        />
      ) : (
        <Section
          title={`${data.questions.length} question${data.questions.length === 1 ? '' : 's'}`}
          description="These required details are missing from your profile. Check suggested answers before saving. One answer can unblock several applications."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {retryButton}
              <Button variant="secondary" size="sm" onClick={() => submit.mutate(false)} disabled={submit.isPending || retryAll.isPending || filled === 0}>
                Save answers
              </Button>
            </div>
          }
          bodyClassName="divide-y divide-line p-0"
        >
          {groupByLevel(shownQuestions).map(([level, questions]) => (
            <div key={level}>
              <h3 className="bg-surface-muted/60 px-5 py-2 text-[12px] font-semibold uppercase tracking-wide text-ink-muted">
                {LEVEL_LABELS[level]} <span className="font-normal normal-case tracking-normal text-ink-faint">· {questions.length}</span>
              </h3>
              <div className="divide-y divide-line">
                {questions.map((question) => (
                  <InboxQuestion
                    key={question.groupKey}
                    question={question}
                    value={answers[question.groupKey] ?? ''}
                    onChange={(value) => setAnswers((current) => ({ ...current, [question.groupKey]: value }))}
                  />
                ))}
              </div>
            </div>
          ))}
          {!showAll && data.questions.length > shownQuestions.length && (
            <button type="button" className="w-full px-5 py-3 text-left text-[13px] font-medium text-brand" onClick={() => setShowAll(true)}>
              Show all {data.questions.length} questions
            </button>
          )}
          {requiredOpen > 0 && (
            <p className="px-5 py-3 text-[13px] text-ink-muted" role="status">
              {requiredOpen} required question{requiredOpen === 1 ? '' : 's'} still open. Applications missing one stay in the inbox; the rest join the queue.
            </p>
          )}
        </Section>
      )}

      <Section
        title={`${data.applications.length} application${data.applications.length === 1 ? '' : 's'} waiting`}
        description={summaryLine(data.summary, stopped.length)}
        actions={
          embedded ? (
            <button type="button" className="text-[13px] font-medium text-brand" onClick={() => setShowApplications((v) => !v)}>
              {showApplications ? 'Hide' : 'Show'}
            </button>
          ) : undefined
        }
      >
        {showApplications && data.applications.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Filter applications needing review">
            {(['all', 'ready', 'answers', 'review', 'verification', 'captcha', 'auto'] as const).map((filter) => (
              <button key={filter} type="button" aria-pressed={applicationFilter === filter} onClick={() => setApplicationFilter(filter)} className={cn('rounded-pill border px-3 py-1.5 text-[12px] font-medium transition', applicationFilter === filter ? 'border-brand bg-brand-soft text-brand' : 'border-line text-ink-muted hover:bg-surface-muted')}>
                {filter === 'all' ? 'All applications' : WAITING_LABEL[filter]} ({data.applications.filter((a) => filter === 'all' || a.waitingFor === filter).length})
              </button>
            ))}
          </div>
        )}
        {showApplications &&
          (data.applications.length === 0 ? (
            <p className="px-5 py-4 text-[13px] text-ink-muted">None right now.</p>
          ) : (
            <ul className="divide-y divide-line">
              {groupByNeeds([...stopped, ...data.applications.filter((a) => a.waitingFor !== 'review')].filter((a) => applicationFilter === 'all' || a.waitingFor === applicationFilter)).map((group) => (
                <li key={group.needs ?? 'other'} className="p-0">
                  {group.label && (
                    <div className="bg-surface-muted/60 px-5 py-1.5 text-[11.5px] font-semibold uppercase tracking-wide text-ink-muted">
                      {group.label} <span className="num font-normal">({group.items.length})</span>
                    </div>
                  )}
                  <ul className="divide-y divide-line">
                    {group.items.map((application) => (
                      <ApplicationRow key={application.id} application={application} batchPending={submit.isPending || retryAll.isPending} />
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          ))}
      </Section>
    </div>
  )
}

const EMBEDDED_QUESTIONS = 8

/** The inbox's sections: the person, their career, this application, the papers (the API's order). */
const LEVEL_ORDER: QuestionLevel[] = ['profile', 'experience', 'education', 'skills', 'application', 'document', 'declaration', 'consent', 'account']

/** The questions under their level headings, in `LEVEL_ORDER`; an older API without levels puts everything under "This application". */
function groupByLevel(questions: ReviewQuestion[]): Array<[QuestionLevel, ReviewQuestion[]]> {
  const groups = new Map<QuestionLevel, ReviewQuestion[]>()
  for (const question of questions) {
    const level: QuestionLevel = question.questionType === 'file' ? 'document' : (question.level ?? 'application')
    const list = groups.get(level)
    if (list) list.push(question)
    else groups.set(level, [question])
  }
  return LEVEL_ORDER.filter((level) => groups.has(level)).map((level) => [level, groups.get(level)!])
}

function summaryLine(summary: { needsAnswers: number; ready: number }, stopped: number): string {
  const parts: string[] = []
  if (summary.needsAnswers) parts.push(`${summary.needsAnswers} need${summary.needsAnswers === 1 ? 's' : ''} answers`)
  if (summary.ready) parts.push(`${summary.ready} ready to submit`)
  if (stopped) parts.push(`${stopped} stopped on the employer's site — queued with your answers when you continue`)
  return parts.length ? parts.join(' · ') : 'Nothing is waiting on you.'
}

function Tile({ label, value, tone }: { label: string; value: number; tone?: 'warning' | 'brand' }) {
  return (
    <div className="card px-4 py-3">
      <div className="text-[12px] font-medium uppercase tracking-wide text-ink-muted">{label}</div>
      <div className={cn('num mt-1 text-[22px] font-semibold', tone === 'warning' && value > 0 ? 'text-warning' : tone === 'brand' && value > 0 ? 'text-brand' : 'text-ink')}>{value}</div>
    </div>
  )
}

/** A list that changes with the country (state, province, region, county): the stored choices were read for whatever country the form opened with. */
const DEPENDS_ON_COUNTRY = /\b(state|province|region|county|prefecture|territory|district)\b/i
const NOT_A_PLACE = /\bcountry\b|\bstatement\b|employment status|marital/i
const dependsOnCountry = (text: string) => DEPENDS_ON_COUNTRY.test(text) && !NOT_A_PLACE.test(text)
const STATE_LIKE = /\b(state|province|region|prefecture|territory)\b/i

function InboxQuestion({ question, value, onChange }: { question: ReviewQuestion; value: string; onChange: (value: string) => void }) {
  const companies = [...new Set(question.askedBy.map((a) => a.company || a.jobTitle))]
  const differ = question.askedBy.some((a) => a.options.length && a.options.join('|') !== question.askedBy[0]!.options.join('|'))
  const place = dependsOnCountry(question.questionText)
  return (
    <div className="px-5 py-5">
      {place ? (
        <CountryPlaceField question={question} value={value} onChange={onChange} />
      ) : (
      <QuestionField
        question={{
          questionKey: question.groupKey,
          questionText: question.questionText,
          questionType: question.questionType,
          options: question.options,
          optionsComplete: question.optionsComplete,
          isRequired: question.isRequired,
          answerValue: question.previousAnswer,
          answerSource: question.previousAnswer ? 'previous_answer' : null,
          rationale: question.rationale,
          suggestedValue: question.suggestedValue,
          tip: question.tip,
          documentKind: question.documentKind,
        }}
        value={value}
        onChange={onChange}
        showSource
        assist={(mode, text) => applicationApi.reviewAssist({ groupKey: question.groupKey, mode, text })}
      />
      )}
      {question.siteError && (
        <div className="mt-2 text-[12.5px] text-warning">
          <p>The portal said: {question.siteError}</p>
          {question.valueTried && <p>Previous answer: {question.valueTried}</p>}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-ink-muted">
        <span>
          Asked by {question.askedBy.length} application{question.askedBy.length === 1 ? '' : 's'}:
        </span>
        {companies.slice(0, 6).map((name) => (
          <span key={name} className="rounded-pill bg-surface-muted px-2 py-0.5 text-ink-soft">
            {name}
          </span>
        ))}
        {companies.length > 6 && <span>+{companies.length - 6} more</span>}
        {differ && !place && <span className="text-ink-faint">· their choices differ; the closest one is picked for each</span>}
        {place && <span className="text-ink-faint">· the form lists the choices for the country picked first; your pick is matched on their site</span>}
      </div>
    </div>
  )
}

/**
 * Country first, then the place: a state / province from the country's own
 * list (India's 36, the United States' 51, …), or a county / district typed
 * in. Starts from the profile's address. The stored choices are not shown:
 * they were read for whatever country the employer's form opened with.
 */
function CountryPlaceField({ question, value, onChange }: { question: ReviewQuestion; value: string; onChange: (value: string) => void }) {
  const profile = useQuery({ queryKey: ['profile'], queryFn: profileApi.get })
  const countries = useQuery({ queryKey: ['countries'], queryFn: () => referenceApi.countries() })
  const [countryCode, setCountryCode] = useState<string>('')
  const home = profile.data?.location
  useEffect(() => {
    if (!countryCode && home?.countryCode) setCountryCode(home.countryCode)
  }, [home?.countryCode, countryCode])
  const states = useQuery({ queryKey: ['states', countryCode], queryFn: () => referenceApi.states(countryCode), enabled: Boolean(countryCode) })
  const stateLike = STATE_LIKE.test(question.questionText)
  // The profile's own place fills the box the first time.
  useEffect(() => {
    if (value) return
    const mine = stateLike ? home?.stateName : home?.countyDistrict
    if (mine) onChange(mine)
  }, [home?.stateName, home?.countyDistrict, stateLike, value, onChange])
  const stateOptions = (states.data ?? []).map((s) => ({ value: s.name, label: s.name }))
  const id = `q-${question.groupKey}`
  return (
    <div>
      <label htmlFor={id} className="mb-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] font-medium leading-snug text-ink">
        <span className="min-w-0 break-words">{question.questionText}</span>
        {question.isRequired ? (
          <span className="rounded-pill border border-line px-1.5 py-px text-[10.5px] font-medium uppercase tracking-wide text-ink-muted">Required</span>
        ) : (
          <span className="text-[12px] font-normal text-ink-faint">(optional)</span>
        )}
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <Combobox
          label="Country"
          appearance="box"
          options={(countries.data ?? []).map((c) => ({ value: c.code, label: c.name }))}
          value={countryCode || null}
          onChange={(code) => {
            setCountryCode(code)
            onChange('')
          }}
          placeholder="Country first…"
          emptyMessage="No country matches"
        />
        {stateLike && (states.isPending || stateOptions.length > 0) ? (
          <Combobox
            id={id}
            label={question.questionText.length > 40 ? 'State / Province' : question.questionText}
            appearance="box"
            options={value && !stateOptions.some((o) => o.value === value) ? [{ value, label: value }, ...stateOptions] : stateOptions}
            value={value || null}
            onChange={onChange}
            placeholder={!countryCode ? 'Pick a country first' : states.isPending ? 'Loading…' : 'Select…'}
            disabled={!countryCode}
            required={question.isRequired}
            emptyMessage="No option matches"
          />
        ) : (
          <div>
            <span className="mb-1 block text-[12.5px] font-medium text-ink-soft">{stateLike ? 'State / Province' : 'County / District'}</span>
            <input
              id={id}
              value={value}
              onChange={(event) => onChange(event.target.value)}
              placeholder={stateLike ? 'Type the state or province' : 'Type the county or district'}
              aria-required={question.isRequired}
              className="field-box min-h-[44px]"
            />
          </div>
        )}
      </div>
    </div>
  )
}

/** The line under a waiting application: the run's last word, or what it waits for and how long. */
function applicationLine(application: ReviewApplication): string {
  if (application.waitingFor === 'auto') {
    const left = timeLeft(application.waitingUntil)
    const base = application.reason || 'Waiting · retries itself'
    return left ? `${base}; ${left}` : base
  }
  if (application.handoff) return application.reason || 'Needs you on the site (CAPTCHA)'
  // The screen record's words: what really blocked the run there (a CAPTCHA, a document, N answers on a step).
  if (application.screen) return application.screen.parked ? `${application.screen.blocker.message} · the page is open and waits for you` : application.screen.blocker.message
  return application.reason || ''
}

/** The headings the stopped applications are grouped under, in order: what the person has to do. */
const NEEDS_ORDER: ScreenBlockerNeeds[] = ['solve_live', 'answers', 'upload', 'wait', 'retry', 'code_fix', 'nothing']

/** What an application needs of the person, from its screen record; `null` for one without a record. */
function needsOf(application: ReviewApplication): ScreenBlockerNeeds | null {
  if (application.handoff) return 'solve_live'
  return application.screen?.blocker.needs ?? null
}

/** The list grouped by what each application needs; those without a record last, under no heading. */
export function groupByNeeds(applications: ReviewApplication[]): Array<{ needs: ScreenBlockerNeeds | null; label: string | null; items: ReviewApplication[] }> {
  const groups: Array<{ needs: ScreenBlockerNeeds | null; label: string | null; items: ReviewApplication[] }> = []
  for (const needs of NEEDS_ORDER) {
    const items = applications.filter((a) => needsOf(a) === needs)
    if (items.length) groups.push({ needs, label: NEEDS_META[needs].label, items })
  }
  const rest = applications.filter((a) => needsOf(a) === null)
  if (rest.length) groups.push({ needs: null, label: groups.length ? 'Other' : null, items: rest })
  return groups
}

function ApplicationRow({ application, batchPending }: { application: ReviewApplication; batchPending: boolean }) {
  const queryClient = useQueryClient()
  const queue = useMutation({
    mutationFn: async () => {
      if (application.waitingFor === 'ready') {
        const result = await applicationApi.submitReady([application.id])
        if (!result.queued.length) throw new Error('This application is no longer ready. Refresh and check its status.')
      } else await applicationApi.retry(application.id)
    },
    onSuccess: () => {
      toast.success('Application queued', 'It will run when your current application finishes.')
      for (const key of ['applications', 'review-inbox', 'application-stats', 'dashboard-summary']) void queryClient.invalidateQueries({ queryKey: [key] })
    },
    onError: (error: ApiError) => toast.error('Could not queue this application', error.message),
  })
  const line = applicationLine(application)
  const needs = needsOf(application)
  const label = application.handoff ? 'Needs you on the site' : needs && application.screen ? `${WAITING_LABEL[application.waitingFor]} · ${NEEDS_META[needs].label}` : WAITING_LABEL[application.waitingFor]
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3">
      <div className="min-w-0 flex-1">
        <Link to={`/dashboard/applications/${application.id}`} className="text-[14px] font-medium text-ink hover:underline">
          {application.jobTitle || 'Application'}
        </Link>
        <div className="mt-1 break-words text-[12.5px] leading-relaxed text-ink-muted">
          {application.company}
          {line ? ` · ${line}` : ''}
        </div>
      </div>
      <span className={cn('rounded-pill px-2 py-0.5 text-[11.5px] font-medium', WAITING_TONE[application.waitingFor])}>
        {label}
        {application.openRequired ? ` · ${application.openRequired} required` : ''}
      </span>
      {application.lastStatusAt && <span className="text-[12px] text-ink-faint">{relativeTime(application.lastStatusAt)}</span>}
      {(application.waitingFor === 'ready' || (application.waitingFor === 'review' && application.openRequired === 0)) ? (
        <Button size="sm" variant="secondary" onClick={() => queue.mutate()} loading={queue.isPending} disabled={batchPending}>Queue application</Button>
      ) : (
        <Link to={`/dashboard/applications/${application.id}`} className="text-[13px] font-medium text-brand">{application.waitingFor === 'answers' ? 'Answer questions' : 'View details'}</Link>
      )}
    </li>
  )
}

function SavedAnswers({ entries }: { entries: BankEntry[] }) {
  const queryClient = useQueryClient()
  const forget = useMutation({
    mutationFn: (questionText: string) => profileApi.forgetAnswer(questionText),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['profile-answers'] }),
    onError: (error: ApiError) => toast.error('Could not forget it', error.message),
  })
  if (!entries.length) return <p className="px-5 py-4 text-[13px] text-ink-muted">Nothing saved yet. Answers you give here, or on an application, are kept.</p>
  return (
    <ul className="divide-y divide-line">
      {entries.map((entry) => (
        <li key={entry.questionKey} className="flex flex-wrap items-start gap-x-4 gap-y-1 px-5 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] text-ink">{entry.questionText}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[12.5px] text-ink-muted">
              <span className="font-medium text-ink-soft">{entry.answerValue}</span>
              {entry.answerSource === 'ai' && (
                <span className="inline-flex items-center gap-1 text-brand">
                  <SparkleIcon /> AI
                </span>
              )}
              <span className="text-ink-faint">{relativeTime(entry.updatedAt)}</span>
            </div>
          </div>
          <button type="button" className="text-[12.5px] text-ink-muted underline-offset-4 hover:underline" onClick={() => forget.mutate(entry.questionText)} disabled={forget.isPending}>
            Forget
          </button>
        </li>
      ))}
    </ul>
  )
}
